import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CallBackProps, Placement, Props, STATUS, Step } from 'react-joyride';

import { useResponsive } from 'contexts/ResponsiveContext';
import { NoopT } from 'declarations/types/common';

type OnboardingTourContextT = {
  isRunningTour: boolean;
  steps: Step[];
  handleStartTourClick: NoopT;
  handleJoyrideCallback: Props['callback'];
};

type OnboardingTourProviderPropsT = {
  children: ReactNode;
};

const contextDefaultValue = {
  isRunningTour: false,
  steps: [],
  handleJoyrideCallback: () => undefined,
  handleStartTourClick: () => undefined,
};

const OnboardingTourContext = createContext<OnboardingTourContextT>(contextDefaultValue);

const OnboardingTourProvider: FC<OnboardingTourProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const [isRunningTour, setIsRunningTour] = useState(false);

  /**
   * Variables
   */
  const steps = useMemo(
    () => [
      ...(isMobile
        ? [
            {
              target: '.collapse-report-btn',
              content: 'Click to open the report section',
              disableBeacon: true,
              spotlightClicks: true,
              spotlightPadding: 6,
              offset: 4,
            },
          ]
        : []),
      {
        target: '.dealer-report',
        content: 'Review dealer monthly report',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.notifications',
        content: 'Click the bell icon to review notifications',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 2,
        offset: 4,
      },
      {
        target: '.user-card',
        content: 'Click on user avatar to open menu',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.open-ticket-btn',
        content: 'Click button to open a new ticket',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '#searchRO',
        content: 'Try to search tickets by RO Number',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.filters',
        content: 'Try to filter tickets',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.column-picker',
        content: 'Try to customize tickets table',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.sorting',
        content: 'Try to sort tickets',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 6,
        offset: 4,
      },
      {
        target: '.ticket-row',
        content: 'Click on row to open ticket details',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 0,
        offset: 4,
      },
      ...(isMobile
        ? [
            {
              target: '.collapse-sidebar-btn',
              content: 'Click to open the sidebar',
              disableBeacon: true,
              spotlightClicks: true,
              spotlightPadding: 6,
              offset: 4,
            },
          ]
        : []),
      {
        target: '.roof-tops-list',
        content: 'Try to filter tickets by Rooftop',
        disableBeacon: true,
        spotlightClicks: true,
        spotlightPadding: 0,
        offset: 4,
        placement: 'right-end' as Placement,
      },
    ],
    [isMobile]
  );

  /**
   * Handlers
   */
  const handleStartTourClick = useCallback(() => {
    setIsRunningTour(true);
  }, []);
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setIsRunningTour(false);
    }
  }, []);

  return (
    <OnboardingTourContext.Provider
      value={{ isRunningTour, steps, handleStartTourClick, handleJoyrideCallback }}
    >
      {children}
    </OnboardingTourContext.Provider>
  );
};

const useOnboardingTour = () => useContext(OnboardingTourContext);

export { OnboardingTourProvider, useOnboardingTour };
